// api.js - FIXED Production URL Detection + SUPERADMIN Role Support
// Utility functions for backend API calls

// FIXED: Backend URL determination for production
// Backend URL is decided by the site address, so the same build works on staging, live and localhost
export function getBackendUrl() {
  const hostname = window.location.hostname;

  // Staging
  if (hostname === 'portalstaging.manurevasolutions.com') {
    return 'https://api.leave-management.manurevasolutions.com';
  }

 

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_API_URL || 'http://localhost:5000';
  }

  // Unknown host: explicit override only, otherwise fail loudly
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  console.error(`getBackendUrl: unknown host "${hostname}"`);
  return 'http://localhost:5000';
}

// ✅ NEW: Normalize role for consistent display
export function normalizeRole(role) {
  if (!role) return '';
  
  const roleStr = String(role).toUpperCase().trim();
  
  console.log('🔄 [NORMALIZE ROLE] Input:', role, '→ Upper:', roleStr);
  
  // Map common variations to standard format
  const roleMap = {
    'SUPERADMIN': 'Superadmin',
    'SUPER_ADMIN': 'Superadmin',
    'SUPER ADMIN': 'Superadmin',
    'SUPERADMINISTRATOR': 'Superadmin',
    'ADMIN': 'CEO',
    'ADMINISTRATOR': 'CEO',
    'CEO': 'CEO',
    'HR': 'HR',
    'HUMAN RESOURCES': 'HR',
    'HUMAN_RESOURCES': 'HR',
    'MANAGER': 'Manager',
    'EMPLOYEE': 'Employee',
    'CONSULTANT': 'Consultant',
    'NETWORK_MANAGER': 'Network Manager',
    'NETWORK MANAGER': 'Network Manager',
    'HOUSEKEEPING': 'Housekeeping'
  };
  
  const normalized = roleMap[roleStr] || role;
  console.log('✅ [NORMALIZE ROLE] Output:', normalized);
  
  return normalized;
}
// ✅ NEW: Get redirect path based on role - SUPERADMIN FIRST
export function getRoleBasedRedirectPath(userRole) {
  console.log('🎯 [API] getRoleBasedRedirectPath called with:', userRole);
  
  // 🔥 FIX: Direct mapping with uppercase normalization
  const roleUpper = String(userRole || '').toUpperCase().trim();
  
  const roleRedirects = {
    'SUPERADMIN': '/Admindashboard',
    'CEO': '/Attendance',
    'ADMIN': '/Attendance',
    'HR': '/Hrdashboard',
    'MANAGER': '/managerdashboard',
    'EMPLOYEE': '/Worker',
    'CONSULTANT': '/Timesheet',
    'NETWORK_MANAGER': '/NetworkManager',
    'NETWORK MANAGER': '/NetworkManager',
    'HOUSEKEEPING': '/Worker'
  };
  
  // Try exact uppercase match first
  if (roleRedirects[roleUpper]) {
    console.log('✅ [API] Found redirect:', roleRedirects[roleUpper]);
    return roleRedirects[roleUpper];
  }
  
  // Fallback: Try normalizeRole
  const normalizedRole = normalizeRole(userRole);
  const normalizedUpper = String(normalizedRole || '').toUpperCase().trim();
  
  if (roleRedirects[normalizedUpper]) {
    console.log('✅ [API] Found redirect via normalized role:', roleRedirects[normalizedUpper]);
    return roleRedirects[normalizedUpper];
  }
  
  // Last resort fallback
  console.warn('⚠️ [API] No redirect found for role, defaulting to /Worker');
  return '/Worker';
}

export async function fetchUser() {
  const backendUrl = getBackendUrl();
  const url = `${backendUrl}/api/user`;
  
  console.log('Fetching user from:', url);
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include', // This is crucial for sending cookies
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      // Add cache control to prevent caching issues
      cache: 'no-cache'
    });
    
    console.log('Fetch user response status:', response.status);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));
    
    // Log response body for debugging (only for non-200 responses)
    if (!response.ok) {
      const responseText = await response.clone().text();
      console.log('Error response body:', responseText);
    }
    
    return response;
  } catch (error) {
    console.error('Error fetching user:', error);
    throw error;
  }
}

export async function logoutUser() {
  const backendUrl = getBackendUrl();
  const url = `${backendUrl}/auth/logout`;
  
  console.log('Logging out from:', url);
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });
    
    console.log('Logout response status:', response.status);
    return response;
  } catch (error) {
    console.error('Error during logout:', error);
    throw error;
  }
}

// Generic API call function
export async function apiCall(endpoint, options = {}) {
  const backendUrl = getBackendUrl();
  const url = `${backendUrl}${endpoint}`;
  
  // Log the URL being called for debugging
  console.log('API Call:', url);
  
  const defaultOptions = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    mode: 'cors'
  };
  
  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers
    }
  };

  try {
    console.log('Making API call:', { url, method: options.method || 'GET' });
    const response = await fetch(url, finalOptions);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error:', errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
}

// Additional utility functions for common API operations

// Function to handle Google OAuth login
export function loginWithGoogle() {
  const backendUrl = getBackendUrl();
  const loginUrl = `${backendUrl}/auth/google`;
  
  console.log('Redirecting to Google OAuth:', loginUrl);
  window.location.href = loginUrl;
}

// Function to check authentication status
export async function checkAuthStatus() {
  try {
    const response = await fetchUser();
    return response.ok;
  } catch (error) {
    console.error('Error checking auth status:', error);
    return false;
  }
}

// Function to handle API responses with JSON parsing
export async function handleApiResponse(response) {
  try {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    return await response.text();
  } catch (error) {
    console.error('Error handling API response:', error);
    throw error;
  }
}

// Function to make authenticated API calls with automatic JSON parsing
export async function authenticatedApiCall(endpoint, options = {}) {
  const response = await apiCall(endpoint, options);
  return await handleApiResponse(response);
}

// ✅ NEW: Check if user has required role(s)
export function hasRole(userRole, requiredRoles) {
  if (!userRole) return false;
  
  const normalizedUserRole = normalizeRole(userRole);
  const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
  const normalizedRequiredRoles = roles.map(r => normalizeRole(r));
  
  return normalizedRequiredRoles.includes(normalizedUserRole);
}

// ✅ NEW: Check if user is admin (Superadmin or CEO)
export function isAdmin(userRole) {
  const normalizedRole = normalizeRole(userRole);
  return ['Superadmin', 'CEO'].includes(normalizedRole);
}

// ✅ NEW: Check if user is manager or higher
export function isManagerOrHigher(userRole) {
  const normalizedRole = normalizeRole(userRole);
  return ['Superadmin', 'CEO', 'HR', 'Manager'].includes(normalizedRole);
}

// ✅ NEW: Get available dashboards based on role
export function getAvailableDashboards(userRole) {
  const normalizedRole = normalizeRole(userRole);
  
  const dashboardAccess = {
    'Superadmin': [
      { name: 'Admin Dashboard', path: '/Admindashboard', icon: '🛡️', primary: true },
      { name: 'CEO Dashboard', path: '/Attendance', icon: '📊', primary: false },
      { name: 'HR Dashboard', path: '/Hrdashboard', icon: '👥', primary: false },
      { name: 'Manager Dashboard', path: '/managerdashboard', icon: '📋', primary: false },
      { name: 'Worker Dashboard', path: '/Worker', icon: '👤', primary: false }
    ],
    'CEO': [
      { name: 'CEO Dashboard', path: '/Attendance', icon: '📊', primary: true },
      { name: 'HR Dashboard', path: '/Hrdashboard', icon: '👥', primary: false },
      { name: 'Manager Dashboard', path: '/managerdashboard', icon: '📋', primary: false },
      { name: 'Worker Dashboard', path: '/Worker', icon: '👤', primary: false }
    ],
    'HR': [
      { name: 'HR Dashboard', path: '/Hrdashboard', icon: '👥', primary: true },
      { name: 'Worker Dashboard', path: '/Worker', icon: '👤', primary: false }
    ],
    'Manager': [
      { name: 'Manager Dashboard', path: '/managerdashboard', icon: '📋', primary: true },
      { name: 'Worker Dashboard', path: '/Worker', icon: '👤', primary: false }
    ],
    'Employee': [
      { name: 'Worker Dashboard', path: '/Worker', icon: '👤', primary: true }
    ],
    'Consultant': [
      { name: 'Timesheet', path: '/Timesheet', icon: '⏰', primary: true }
    ],
    'Network Manager': [
      { name: 'Network Manager Dashboard', path: '/NetworkManager', icon: '🌐', primary: true }
    ],
    'Housekeeping': [
      { name: 'Worker Dashboard', path: '/Worker', icon: '👤', primary: true }
    ]
  };
  
  return dashboardAccess[normalizedRole] || dashboardAccess['Employee'];
}

// ✅ NEW: Format user data with normalized role
export function formatUserData(userData) {
  if (!userData) return null;
  
  return {
    ...userData,
    role: normalizeRole(userData.role),
    redirect_path: getRoleBasedRedirectPath(userData.role),
    is_admin: isAdmin(userData.role),
    is_manager: isManagerOrHigher(userData.role),
    available_dashboards: getAvailableDashboards(userData.role)
  };
}